// Movimento suave dos marcadores do mapa: snapshot interpolation com atraso fixo.
//
// A telemetria chega em cadência que varia por device (o publisher nominal é 2 s,
// mas devices reais medidos publicam a cada 4-5 s) e com jitter de ingestão. Em vez
// de teleportar o marcador a cada mensagem, cada device guarda um buffer de posições
// com timestamp e o mapa renderiza o instante `agora - atraso`, interpolando entre os
// dois snapshots que cercam esse instante. O atraso é ADAPTATIVO por device: 1.3× o
// intervalo típico entre snapshots (EWMA), para o próximo snapshot quase sempre já
// ter chegado quando for preciso interpolar até ele.
//
// Sem snapshot novo, extrapola por dead reckoning (última velocidade/rumo) por um
// teto curto e congela: melhor parar do que inventar movimento. Quando o dado real
// volta e contradiz a extrapolação, a posição exibida converge por um low-pass de
// ~SMOOTH_TAU_MS em vez de saltar; acima de TELEPORT_M o salto é honesto (primeiro
// fix, aba que voltou do background, realocação real).

const DEFAULT_DELAY_MS = 3500
const MIN_DELAY_MS = 2500
const MAX_DELAY_MS = 7000
const DELAY_FACTOR = 1.3
const MAX_EXTRAPOLATION_MS = 2500
const KEEP_MS = 20000
const SMOOTH_TAU_MS = 350
const TELEPORT_M = 150
// Intervalo acima disto é queda de link/telemetria, não cadência: fica fora do EWMA.
const GAP_MS = 15000
// Devices reais emitem RAJADAS de mensagens duplicadas (5 em 10 ms, mesma posição).
// Entrar no buffer criaria nós colados (tangentes explodem na interpolação) e os
// intervalos de 1 ms esmagariam o EWMA, encolhendo o atraso até o buffer secar.
const BURST_MS = 800

const M_PER_DEG_LAT = 111320

function approxDistM(aLat, aLng, bLat, bLng) {
  const dLat = (bLat - aLat) * M_PER_DEG_LAT
  const dLng = (bLng - aLng) * M_PER_DEG_LAT * Math.cos(aLat * Math.PI / 180)
  return Math.hypot(dLat, dLng)
}

// Hermite cúbica (Catmull-Rom com nós não uniformes) entre b e c, vizinhos a e d
// opcionais para as tangentes. Passa exatamente pelos snapshots e faz a curva em
// arco em vez de quebrar nas esquinas — com amostras a cada 4-5 s a poligonal
// linear corta as curvas de um jeito visível.
function hermite(a, b, c, d, t) {
  const h = c.t - b.t
  const u = (t - b.t) / h
  const tanB = a ? { lat: (c.lat - a.lat) / (c.t - a.t), lng: (c.lng - a.lng) / (c.t - a.t) }
                 : { lat: (c.lat - b.lat) / h, lng: (c.lng - b.lng) / h }
  const tanC = d ? { lat: (d.lat - b.lat) / (d.t - b.t), lng: (d.lng - b.lng) / (d.t - b.t) }
                 : { lat: (c.lat - b.lat) / h, lng: (c.lng - b.lng) / h }
  const u2 = u * u, u3 = u2 * u
  const h00 = 2 * u3 - 3 * u2 + 1, h10 = u3 - 2 * u2 + u
  const h01 = -2 * u3 + 3 * u2, h11 = u3 - u2
  return {
    lat: h00 * b.lat + h10 * h * tanB.lat + h01 * c.lat + h11 * h * tanC.lat,
    lng: h00 * b.lng + h10 * h * tanB.lng + h01 * c.lng + h11 * h * tanC.lng,
  }
}

// Posição-alvo no instante t: interpolação entre snapshots, ou dead reckoning
// limitado além do último.
function targetAt(snaps, t) {
  const first = snaps[0]
  const last = snaps[snaps.length - 1]
  if (t <= first.t) return { lat: first.lat, lng: first.lng }
  if (t < last.t) {
    for (let i = snaps.length - 2; i >= 0; i--) {
      const a = snaps[i]
      if (a.t > t) continue
      return hermite(snaps[i - 1] || null, a, snaps[i + 1], snaps[i + 2] || null, t)
    }
    return { lat: first.lat, lng: first.lng }
  }
  if (last.speed > 1 && last.heading != null) {
    const dt = Math.min(t - last.t, MAX_EXTRAPOLATION_MS) / 1000
    const d = (last.speed / 3.6) * dt
    const rad = last.heading * Math.PI / 180
    return {
      lat: last.lat + (d * Math.cos(rad)) / M_PER_DEG_LAT,
      lng: last.lng + (d * Math.sin(rad)) / (M_PER_DEG_LAT * Math.cos(last.lat * Math.PI / 180)),
    }
  }
  return { lat: last.lat, lng: last.lng }
}

export class MarkerAnimator {
  constructor() {
    this.buffers = new Map()
  }

  // snap = { lat, lng, heading, speed, t } com t em ms no relógio LOCAL.
  // Dedupe por t: o mesmo fix chega repetido pelo snapshot de devices de 10 s.
  push(name, snap) {
    const b = this.buffers.get(name)
    if (!b) { this.seed(name, snap); return }
    const snaps = b.snaps
    const interval = snap.t - snaps[snaps.length - 1].t
    if (interval < BURST_MS) return
    if (interval < GAP_MS) b.ewmaMs = b.ewmaMs == null ? interval : b.ewmaMs * 0.8 + interval * 0.2
    snaps.push(snap)
    const cutoff = snap.t - KEEP_MS
    while (snaps.length > 4 && snaps[0].t < cutoff) snaps.shift()
  }

  // Primeiro avistamento: nasce no lugar, sem deslizar de posição nenhuma.
  seed(name, snap) {
    this.buffers.set(name, { snaps: [snap], displayed: { lat: snap.lat, lng: snap.lng }, sampledAt: 0, ewmaMs: null })
  }

  remove(name) { this.buffers.delete(name) }
  clear() { this.buffers.clear() }

  latest(name) {
    const b = this.buffers.get(name)
    return b ? b.snaps[b.snaps.length - 1] : null
  }

  // Posição a renderizar "agora" (ms locais); o atraso adaptativo é descontado aqui.
  sample(name, now) {
    const b = this.buffers.get(name)
    if (!b) return null
    const delay = b.ewmaMs == null ? DEFAULT_DELAY_MS
      : Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, b.ewmaMs * DELAY_FACTOR))
    const tRender = now - delay
    const target = targetAt(b.snaps, tRender)
    const dt = b.sampledAt ? Math.min(Math.max(now - b.sampledAt, 0), 1000) : Infinity
    b.sampledAt = now
    const cur = b.displayed
    if (dt === Infinity || approxDistM(cur.lat, cur.lng, target.lat, target.lng) > TELEPORT_M) {
      b.displayed = target
      return target
    }
    const f = Math.min(1, dt / SMOOTH_TAU_MS)
    b.displayed = { lat: cur.lat + (target.lat - cur.lat) * f, lng: cur.lng + (target.lng - cur.lng) * f }
    return b.displayed
  }
}
